import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useAmbience } from "../hooks/useAmbience";

/*
  Cinematic, scroll-driven "movie". The section is tall; a sticky viewport
  plays through scenes as the visitor scrolls. Each scene's local progress
  (0 -> 1) drives a Ken Burns push-in, image drift, and text parallax.
*/
export default function ScrollJourney({ chapters, progressLabel = "Follow the bean", accent = "#b08a5f", topSlot = null, ambience = null }) {
  const wrapRef = useRef(null);
  const [st, setSt] = useState({ active: 0, local: 0, progress: 0 });
  const [sound, setSound] = useState(false);
  const cue = useAmbience(ambience, sound);
  const n = chapters.length;

  useEffect(() => { cue(st.active === n - 1 ? "final" : "scene"); }, [st.active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight - window.innerHeight;
        const scrolled = Math.min(Math.max(-rect.top, 0), total);
        const p = total > 0 ? scrolled / total : 0;
        const pos = p * n;
        const active = Math.min(n - 1, Math.floor(pos + 0.0001));
        const local = Math.min(1, pos - active);
        setSt({ active, local, progress: p });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [n]);

  const { active, local, progress } = st;
  const isLast = active === n - 1;
  const scale = 1.04 + local * 0.14;
  const drift = -local * 4;
  const fadeIn = active === 0 ? 1 : Math.min(1, local / 0.12);
  const fadeOut = isLast ? 1 : Math.min(1, (1 - local) / 0.14);
  const textOpacity = Math.min(fadeIn, fadeOut);
  const textY = (0.5 - local) * 40;

  const layerTransform = (i) => {
    if (i === active) return `scale(${scale.toFixed(4)}) translateY(${drift.toFixed(3)}%)`;
    if (i < active) return "scale(1.18) translateY(-4%)";
    return "scale(1.04) translateY(0%)";
  };

  return (
    <section ref={wrapRef} style={{ height: `${n * 100}vh` }} className="relative" data-testid="scroll-journey">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#10271a] grain">
        {chapters.map((ch, i) => (
          <div key={i} className={`chapter-layer absolute inset-0 ${i === active ? "is-active" : ""}`}>
            <img src={ch.image} alt={ch.emphasis} className="h-full w-full object-cover" style={{ transform: layerTransform(i) }} loading={i < 2 ? "eager" : "lazy"} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#10271a] via-[#10271a]/50 to-[#10271a]/30" />
            <div className="absolute inset-0 bg-[#10271a]/20" />
          </div>
        ))}

        {topSlot && <div className="absolute top-[88px] md:top-24 left-0 right-0 z-30 flex justify-center px-6">{topSlot}</div>}

        <div className="relative z-10 h-full">
          {chapters.map((ch, i) => (
            <div key={i} className={`absolute inset-0 flex items-center ${i === active ? "chapter-active" : ""}`} style={{ pointerEvents: i === active ? "auto" : "none" }}>
              <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10">
                <div className="max-w-3xl" style={i === active ? { opacity: textOpacity, transform: `translateY(${textY.toFixed(2)}px)`, transition: "opacity 0.25s linear, transform 0.25s linear" } : undefined} data-testid={i === active ? "active-scene" : undefined}>
                  <p className="chapter-text text-[12px] md:text-sm tracking-[0.25em] uppercase mb-6" style={{ color: accent }}>{ch.kicker}</p>
                  <h2 className="chapter-text font-display text-[#f6f1e7] text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light" data-testid={i === active ? "scene-heading" : undefined}>
                    {ch.pre} <em className="italic text-[#e9d9bf]">{ch.emphasis}</em>
                    {ch.post ? ` ${ch.post}` : ""}
                  </h2>
                  <p className="chapter-text mt-8 text-[#f6f1e7]/80 text-lg md:text-xl leading-relaxed max-w-xl">{ch.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 z-20 hidden sm:flex flex-col items-center gap-4">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#f6f1e7]/70 [writing-mode:vertical-rl] rotate-180">{progressLabel}</span>
          <div className="h-40 w-[2px] bg-[#f6f1e7]/20 rounded-full overflow-hidden">
            <div className="w-full rounded-full transition-[height] duration-150" style={{ height: `${Math.round(progress * 100)}%`, background: accent }} />
          </div>
          <span className="text-[11px] tabular-nums text-[#f6f1e7]/70">{Math.round(progress * 100)}%</span>
        </div>

        <div className="absolute left-0 right-0 bottom-0 z-20 border-t border-[#f6f1e7]/10 bg-[#10271a]/55 backdrop-blur-md">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between gap-6">
            <div className="flex items-end gap-2 shrink-0">
              <span className="font-display text-[#f6f1e7] text-3xl leading-none" data-testid="scene-counter">0{active + 1}</span>
              <span className="text-[#f6f1e7]/50 text-sm mb-0.5">/ 0{n}</span>
            </div>
            <div className="hidden md:flex flex-1 items-center gap-1.5">
              {chapters.map((_, i) => (
                <span key={i} className="h-[3px] flex-1 rounded-full overflow-hidden bg-[#f6f1e7]/15">
                  <span className="block h-full" style={{ width: i < active ? "100%" : i === active ? `${local * 100}%` : "0%", background: accent }} />
                </span>
              ))}
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {ambience && (
                <button
                  type="button"
                  onClick={() => setSound((s) => !s)}
                  aria-pressed={sound}
                  aria-label={sound ? "Turn sound off" : "Turn sound on"}
                  data-testid="sound-toggle"
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase transition-colors ${sound ? "border-[#f6f1e7]/60 text-[#f6f1e7]" : "border-[#f6f1e7]/20 text-[#f6f1e7]/60 hover:text-[#f6f1e7]"}`}
                >
                  {sound ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  <span className="hidden sm:inline">{sound ? "Sound on" : "Sound off"}</span>
                </button>
              )}
              <p className="text-[11px] md:text-xs tracking-[0.2em] uppercase text-[#f6f1e7]/80 tabular-nums text-right" data-testid="scene-timecode">
                {chapters[active].time}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
